/**
 * Descarga de archivos Excel (.xlsx) desde el navegador.
 *
 * Decisión de producto (paquete R): los stakeholders regulatorios prefieren
 * Excel sobre CSV — tiene tipado de columnas (fechas, números), fórmulas
 * potenciales en el futuro, y abre sin pasos extras en cualquier máquina.
 *
 * Code-splitting: `xlsx` pesa ~430KB. Lo cargamos dinámicamente solo cuando
 * el operador hace export — la SPA inicial no lo arrastra.
 *
 * Vive en `lib/` porque lo usan varias features (operaciones, recepción).
 */

export interface HojaExcel {
  nombre: string;
  headers: readonly string[];
  rows: readonly (readonly unknown[])[];
}

function anchosDeColumna(headers: readonly string[], rows: readonly (readonly unknown[])[]) {
  // Auto-anchos: para cada columna, calcular el ancho del valor más largo
  // (limitado a 50 chars para que no quede inmanejable).
  const widths: { wch: number }[] = [];
  for (let c = 0; c < headers.length; c++) {
    let max = String(headers[c] ?? '').length;
    for (const row of rows) {
      const cell = row[c];
      let s: string;
      if (cell === null || cell === undefined) s = '';
      else if (typeof cell === 'string') s = cell;
      else if (typeof cell === 'number' || typeof cell === 'boolean' || typeof cell === 'bigint')
        s = String(cell);
      else s = JSON.stringify(cell);
      if (s.length > max) max = s.length;
    }
    widths.push({ wch: Math.min(50, Math.max(8, max + 2)) });
  }
  return widths;
}

/** Genera y dispara la descarga de un .xlsx con una o varias hojas. */
export async function descargarExcelHojas(nombreArchivo: string, hojas: readonly HojaExcel[]) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const hoja of hojas) {
    const aoa: unknown[][] = [hoja.headers as unknown[], ...hoja.rows.map((r) => [...r])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = anchosDeColumna(hoja.headers, hoja.rows);
    // Excel limita el nombre de hoja a 31 caracteres.
    XLSX.utils.book_append_sheet(wb, ws, hoja.nombre.slice(0, 31));
  }
  XLSX.writeFile(wb, nombreArchivo);
}

/** Variante de una sola hoja (la firma histórica de `features/operaciones/filtros`). */
export async function descargarExcel(
  nombreArchivo: string,
  headers: readonly string[],
  rows: readonly (readonly unknown[])[],
  opciones?: { hoja?: string },
): Promise<void> {
  await descargarExcelHojas(nombreArchivo, [{ nombre: opciones?.hoja ?? 'Datos', headers, rows }]);
}
